import {
  IGetTeacherByIdResponseBody,
  IGetTeacherDetailsRequestParams,
  IGetTeachersResponseBody,
  ICreateTeacherRequestBody,
  ICreateTeacherResponseBody,
  IGetAssetId,
  IGetTeacherRosterClassroomsResponse,
  ITeacherTimetableResponse,
  ITeacherClassroomAssignmentsResponse,
  IAddTeacherClassroomsRequest,
} from '../../interface/teacherModel';
import api from '../api';
import apiMultipart from '../api-multipart';

export const getTeacherData = async (
  params: IGetTeacherDetailsRequestParams,
): Promise<IGetTeachersResponseBody> => {
  try {
    const response = await api.get<IGetTeachersResponseBody>(
      `/teacher?perPageRow=${params.perPageRow}&currentPage=${params.currentPage}${params.grade}${params.medium}${params.search}`,
    );
    return response.data;
  } catch (error: any) {
    console.error(error, 'Error fetching teacher data');
    throw new Error(` ${error.message}`);
  }
};
export const createTeacher = async (
  data: ICreateTeacherRequestBody,
): Promise<ICreateTeacherResponseBody> => {
  try {
    // Sending POST request to '/teacher' endpoint with the request body
    const response = await api.post<ICreateTeacherResponseBody>('/teacher', data);
    // Return the response data
    return response.data;
  } catch (error: any) {
    // Logging the error and throwing it with a meaningful message
    console.error(error, 'Error creating teacher');
    // Rethrow the backend body untouched, the way services/classRoom does.
    // `new Error(error.message)` re-wrapped it and lost everything the thunk's
    // normalizer reads, so server-worded errors reached the UI degraded.
    throw error;
  }
};

export const getAssetId = async (
  file: FormData,
  onUploadProgress?: (percent: number) => void,
): Promise<IGetAssetId> => {
  try {
    const response = await apiMultipart.post<IGetAssetId>('/file', file, {
      onUploadProgress: onUploadProgress
        ? (event) => {
            // event.total can be undefined for some browsers/proxies; guard against it.
            if (event.total) {
              onUploadProgress(Math.round((event.loaded * 100) / event.total));
            }
          }
        : undefined,
    });
    return response.data;
  } catch (error: any) {
    console.error(error, 'Error getting asset id');
    throw new Error(`Failed to get asset id: ${error.message}`);
  }
};

export const getTeacherDataByID = async (id: string): Promise<IGetTeacherByIdResponseBody> => {
  try {
    const response = await api.get<IGetTeacherByIdResponseBody>(`/teacher/${id}`);
    return response.data;
  } catch (error: any) {
    console.error(error, 'Error fetching teacher data');
    throw new Error(`Failed to fetch teacher data: ${error.message}`);
  }
};
/**
 * A teacher's roster slots for the Timetable tab on their profile (CVA-1258).
 *
 * `weekStart` selects the view: pass a Sunday (YYYY-MM-DD) for the schedule
 * grid, which navigates week by week; omit it for the flat Grade / Class /
 * Subject / Day table, which shows the whole recurring pattern.
 */
export const getTeacherTimetable = async (
  id: string,
  options: { weekStart?: string; page?: number; pageSize?: number } = {},
): Promise<{ data: ITeacherTimetableResponse }> => {
  try {
    const { weekStart, page, pageSize } = options;
    const response = await api.get<{ data: ITeacherTimetableResponse }>(
      `/teacher/${id}/timetable`,
      {
        params: weekStart
          ? { weekStart }
          : // One-based, matching the rest of the API.
            { page: page ?? 1, pageSize: pageSize ?? 10 },
      },
    );
    return response.data;
  } catch (error: any) {
    console.error(error, 'Error fetching teacher timetable');
    throw new Error(`Failed to fetch teacher timetable: ${error.message}`);
  }
};

/**
 * A page of a teacher's classroom assignments for the Assign Classroom tab
 * (CVA-1263). Paged server-side; `page` is one-based, matching the rest of the
 * API.
 */
export const getTeacherClassroomAssignments = async (
  id: string,
  options: { page?: number; pageSize?: number } = {},
): Promise<{ data: ITeacherClassroomAssignmentsResponse }> => {
  try {
    const response = await api.get<{ data: ITeacherClassroomAssignmentsResponse }>(
      `/teacher/${id}/classrooms`,
      {
        params: { page: options.page ?? 1, pageSize: options.pageSize ?? 10 },
      },
    );
    return response.data;
  } catch (error: any) {
    console.error(error, 'Error fetching teacher classroom assignments');
    throw new Error(`Failed to fetch teacher classroom assignments: ${error.message}`);
  }
};

/**
 * Adds classroom assignments to a teacher (CVA-1259).
 *
 * POST because the body is a set of ADDITIONS, not the complete desired state —
 * the table is paged, so the client cannot know the full set. The response
 * carries the first page of the result.
 */
export const addTeacherClassroomAssignments = async (
  id: string,
  body: IAddTeacherClassroomsRequest,
): Promise<{ data: ITeacherClassroomAssignmentsResponse }> => {
  try {
    const response = await api.post<{ data: ITeacherClassroomAssignmentsResponse }>(
      `/teacher/${id}/classrooms`,
      body,
    );
    return response.data;
  } catch (error: any) {
    console.error(error, 'Error adding teacher classroom assignments');
    // Rethrow the backend body untouched — the interceptor already rejects with
    // it, and re-wrapping loses the server's wording for this endpoint's 400s.
    throw error;
  }
};

export const deleteTeacherDataByID = async (id: string): Promise<IGetTeacherByIdResponseBody> => {
  try {
    const response = await api.delete<IGetTeacherByIdResponseBody>(`/teacher/${id}`);
    return response.data;
  } catch (error: any) {
    console.error(error, 'Error fetching teacher data');
    throw new Error(`Failed to fetch teacher data: ${error.message}`);
  }
};

export const putEditTeacher = async (
  id: string,
  requestBody: ICreateTeacherRequestBody,
): Promise<IGetTeacherByIdResponseBody> => {
  try {
    const response = await api.put<IGetTeacherByIdResponseBody>(`/teacher/${id}`, requestBody);
    return response.data;
  } catch (error: any) {
    console.error(error, 'Error posting teacher data');
    // Rethrow the backend body untouched — see createTeacher above.
    throw error;
  }
};

/**
 * CVA-4153/4155 — classrooms the CALLING teacher is rostered to as a
 * subject-teacher (`isSubject: true`, resolved server-side via
 * `roster_detail` — the same mechanism the mobile app already relies on via
 * `useMyClassrooms`). Backs the Add Exam wizard's Curriculum/Grade/Classroom
 * pickers for a Teacher (non EXAM_MANAGEMENT) caller. Deliberately calls
 * `GET /me/classrooms` rather than `GET /teacher/classroom?type=roster` —
 * the mobile app moved off that endpoint after finding it broken (see
 * `ClassVeewApp/src/services/Awards/index.ts`'s removal comment) in favor of
 * this one, and this one already returns grade/curriculum context inline, so
 * no backend change was needed to support this picker. No try/catch here —
 * errors are normalized by the calling thunk (see
 * `fetchTeacherRosterClassrooms` in `redux/slices/teacherSliceModel.tsx`).
 */
export const getTeacherRosterClassrooms =
  async (): Promise<IGetTeacherRosterClassroomsResponse> => {
    const response = await api.get<IGetTeacherRosterClassroomsResponse>('/me/classrooms', {
      params: { isSubject: true },
    });
    return response.data;
  };
